import axios from 'axios'
import client from './client'
import type { IndustryListItem, Industry, Agent } from '../types'

const BASE = import.meta.env.VITE_API_URL

/**
 * Phase 2: industry-agent endpoints now return Agent (unified shape) rather
 * than the deprecated IndustryAgent shape. Backend responses still come back
 * with `agent_id` (string) and string-typed prices; the adapter normalizes
 * to `id` + numeric prices and tags `agent_kind: 'industry'`.
 *
 * Both `id` and `agent_id` are populated so deferred consumers (Phase 3)
 * that still read `agent_id` keep working.
 */
function adaptIndustryAgent(raw: Record<string, unknown>): Agent {
  const slug = String(raw['agent_id'] ?? raw['id'] ?? '')
  const monthly = raw['monthly_price']
  const yearly = raw['yearly_price']
  return {
    id: slug,
    agent_id: slug,
    name: String(raw['name'] ?? ''),
    role: String(raw['role'] ?? ''),
    dept: String(raw['dept'] ?? ''),
    emoji: String(raw['emoji'] ?? ''),
    color: String(raw['color'] ?? ''),
    image_url: String(raw['image_url'] ?? ''),
    description: String(raw['description'] ?? ''),
    monthly_price: typeof monthly === 'number' ? monthly : parseFloat(String(monthly ?? '0')),
    yearly_price: typeof yearly === 'number' ? yearly : parseFloat(String(yearly ?? '0')),
    tagline: String(raw['tagline'] ?? ''),
    hero_headline: String(raw['hero_headline'] ?? ''),
    what_i_do: String(raw['what_i_do'] ?? ''),
    capabilities: (raw['capabilities'] as string[]) ?? [],
    use_cases: (raw['use_cases'] as Array<{ title: string; problem: string; solution: string }>) ?? [],
    industries_served: (raw['industries_served'] as string[]) ?? [],
    faqs: (raw['faqs'] as Array<{ question: string; answer: string }>) ?? [],
    agent_kind: 'industry',
    industry_slug: raw['industry_slug'] ? String(raw['industry_slug']) : undefined,
    industry_name: raw['industry_name'] ? String(raw['industry_name']) : undefined,
    owned: typeof raw['owned'] === 'boolean' ? (raw['owned'] as boolean) : undefined,
  }
}

export async function listIndustries(): Promise<IndustryListItem[]> {
  const { data } = await axios.get(`${BASE}/api/industries/`)
  return data as IndustryListItem[]
}

export async function getIndustry(slug: string): Promise<Industry> {
  const { data } = await axios.get(`${BASE}/api/industries/${slug}/`)
  return data as Industry
}

export async function getIndustryAgents(slug: string): Promise<Agent[]> {
  const { data } = await axios.get(`${BASE}/api/industries/${slug}/agents/`)
  return (data as Array<Record<string, unknown>>).map(adaptIndustryAgent)
}

export async function getMyIndustryAgents(): Promise<Agent[]> {
  const { data } = await client.get('/api/my-industry-agents/')
  return (data as Array<Record<string, unknown>>).map(adaptIndustryAgent)
}

export async function getIndustryAgent(agentId: string): Promise<Agent> {
  const { data } = await axios.get(`${BASE}/api/industry-agents/${agentId}/`)
  return adaptIndustryAgent(data as Record<string, unknown>)
}

export async function sendIndustryMessage(agentId: string, message: string, sessionId?: string | null) {
  const { data } = await client.post(`/api/industry-agents/${agentId}/chat/`, {
    message,
    session_id: sessionId ?? null,
  })
  return data as {
    response: string
    agent_id: string
    agent_name: string
    agent_emoji: string
    agent_color: string
    model_used: string
    is_lite_mode: boolean
    handoffs: Array<{ agent_id: string; agent_name: string; agent_emoji: string; agent_color: string; content: string }>
    session_title: string
    session_id: string
    trial_messages_remaining: number | null
  }
}
